import styled from "@emotion/styled";

import { Button, DialogActions, DialogContent, FONT_WEIGHT_SEMI_BOLD } from "@skilitics/designer-ui-components";
import { useDynamicFormatString, useFormatMessage } from "@skilitics/localization";

import { upgradePrompts, type FeatureName } from "./PremiumFeatures";

type Props = {
  feature: FeatureName;
};

export const UpgradePrompt = ({ feature }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const formatDynamicString = useDynamicFormatString();
  const { description, title, imgUrl } = upgradePrompts[feature];

  return (
    <UpgradePromptContainer>
      <ImageWrapper>{imgUrl && <img src={imgUrl} alt={formatDynamicString(title)} />}</ImageWrapper>
      <Content>
        <Title>{formatDynamicString(title)}</Title>
        <Description>{formatDynamicString(description)}</Description>
      </Content>
      <Footer>
        <UpgradeButton variant="contained" href="/settings/billing">
          {formatMessage("upgrade")}
        </UpgradeButton>
      </Footer>
    </UpgradePromptContainer>
  );
};

const UpgradePromptContainer = styled.div`
  width: 380px;
`;

const Content = styled(DialogContent)`
  border-top: solid 1px ${({ theme }) => theme.outlineSoft};
  padding-bottom: 5px;
`;

const Title = styled.h3`
  margin: 0 0 10px;
  padding: 0;
  font-size: 16px;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

const Description = styled.p`
  margin: 0;
  padding: 0;
  font-size: 14px;
  line-height: 1.4;
`;

const ImageWrapper = styled.div`
  display: flex;
  padding: 20px 20px 0;
  background: linear-gradient(135deg, ${({ theme }) => theme.warning}, ${({ theme }) => theme.error});

  img {
    width: 100%;
  }
`;

const Footer = styled(DialogActions)`
  padding: 15px;
`;

// Note: this is not a LinkButton, as quite often this sits outside of router context.
const UpgradeButton = styled(Button)`
  background: linear-gradient(220deg, ${({ theme }) => theme.primary}, ${({ theme }) => theme.secondary});
  border-radius: 20px;
`;

const formatStrings = {
  upgrade: {
    en: "Upgrade",
  },
};
